import { useTranslation } from 'react-i18next'
import { useNavigate } from '@tanstack/react-router'
import { AppIcon } from '../ui/icon'
import { mdiFileDocumentMultiple, mdiOpenInNew, mdiFolderZipOutline } from '@mdi/js'
import { Badge } from '../ui/badge'
import { useJobs } from '../../api/jobs'

/** formatPageRange renders a sorted 1-based page set as "3-5" / "1-2, 10". */
export function formatPageRange(pages: number[] | undefined | null): string {
    if (!pages || pages.length === 0) return ''
    const sorted = pages.slice().sort((a, b) => a - b)
    const parts: string[] = []
    let start = sorted[0]
    let prev = sorted[0]
    for (const p of sorted.slice(1)) {
        if (p === prev + 1) {
            prev = p
            continue
        }
        parts.push(start === prev ? `${start}` : `${start}-${prev}`)
        start = p
        prev = p
    }
    parts.push(start === prev ? `${start}` : `${start}-${prev}`)
    return parts.join(', ')
}

interface ChildSummary {
    uid: string
    name: string
    document_type?: { name?: Record<string, string>; code?: string } | null
    parent_document_pages?: number[]
}

interface DocumentBundleContentsProps {
    org: string
    children: ChildSummary[]
    /**
     * Set for a deposit archive (spec 2026-08-16-06): the card becomes
     * "Extracted files", drops the page badges (an archive entry has no pages in
     * its parent) and surfaces what the extraction skipped.
     */
    isArchive?: boolean
    /** Exact number of live children; the embedded list is capped at 200. */
    totalCount?: number
    /** Archive document UID, used to find its latest deposit_extract run. */
    documentUid?: string
}

/**
 * DepositExtractSummary shows the two numbers the extraction job reports but the
 * child list cannot: entries skipped because we already held the exact file, and
 * entries skipped as OS junk.
 */
function DepositExtractSummary({ org, documentUid }: { org: string; documentUid: string }) {
    const { t } = useTranslation('documents')
    const { data } = useJobs(org, {
        type: 'deposit_extract',
        target_entity_type: 'document',
        target_entity_uid: documentUid,
        limit: 1,
    })

    const output = data?.jobs?.[0]?.last_execution?.output as
        | {
              skipped_duplicate_count?: number
              skipped_junk?: number
              skipped_deep_archives?: number
              failed_entry_count?: number
              failed_entries?: { path: string; error: string }[]
              error?: string
          }
        | undefined
    if (!output) return null

    const duplicates = output.skipped_duplicate_count || 0
    const junk = output.skipped_junk || 0
    const deep = output.skipped_deep_archives || 0
    const failed = output.failed_entry_count || 0
    if (!duplicates && !junk && !deep && !failed && !output.error) return null

    return (
        <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="archive-extract-summary">
            {duplicates > 0 && (
                <Badge variant="outline" data-testid="archive-skipped-duplicates">
                    {t('archive.skipped_duplicates', { count: duplicates })}
                </Badge>
            )}
            {junk > 0 && (
                <Badge variant="outline" data-testid="archive-skipped-junk">
                    {t('archive.skipped_junk', { count: junk })}
                </Badge>
            )}
            {deep > 0 && (
                <Badge variant="outline" data-testid="archive-skipped-deep">
                    {t('archive.skipped_deep', { count: deep })}
                </Badge>
            )}
            {failed > 0 && (
                <details className="w-full text-xs" data-testid="archive-failed-entries">
                    <summary className="cursor-pointer">
                        <Badge variant="destructive">{t('archive.failed_entries', { count: failed })}</Badge>
                    </summary>
                    <p className="mt-2 text-muted-foreground">{t('archive.failed_entries_hint')}</p>
                    <ul className="mt-1 space-y-1">
                        {(output.failed_entries ?? []).map((entry) => (
                            <li key={entry.path} className="break-all">
                                <span className="font-medium">{entry.path}</span>
                                <span className="text-muted-foreground"> — {entry.error}</span>
                            </li>
                        ))}
                    </ul>
                </details>
            )}
            {output.error && (
                <span className="text-xs text-destructive" data-testid="archive-extract-error">
                    {t('archive.extract_failed', { error: output.error })}
                </span>
            )}
        </div>
    )
}

/**
 * DocumentBundleContents renders the "Bundle contents" card on a MIXED parent,
 * listing each child document with a page badge and a link to the child. For a
 * deposit archive it renders the same list as "Extracted files".
 */
export function DocumentBundleContents({ org, children, isArchive, totalCount, documentUid }: DocumentBundleContentsProps) {
    const { t, i18n } = useTranslation('documents')
    const navigate = useNavigate()

    // An archive keeps its card even before the first child exists, so a still
    // running — or failed — extraction is visible instead of an empty page.
    if ((!children || children.length === 0) && !isArchive) return null

    const shownCount = totalCount ?? children.length
    const truncated = shownCount > children.length

    const typeName = (child: ChildSummary): string => {
        const n = child.document_type?.name
        if (n) {
            return n[i18n.language] || n.en || n.fr || child.document_type?.code || t('bundle.untyped')
        }
        return t('bundle.untyped')
    }

    return (
        <div className="bg-card rounded-lg border border-border p-4" data-testid={isArchive ? 'archive-contents-card' : 'bundle-contents-card'}>
            <div className="flex items-center gap-2 mb-1">
                <AppIcon tone="muted" path={isArchive ? mdiFolderZipOutline : mdiFileDocumentMultiple} size="md" />
                <h2 className="text-lg font-semibold text-foreground">
                    {isArchive ? t('archive.contents_title') : t('bundle.contents_title')}
                </h2>
                <Badge variant="secondary" data-testid="bundle-child-count">
                    {shownCount}
                </Badge>
            </div>
            <p className="text-sm text-muted-foreground mb-3">
                {isArchive ? t('archive.contents_description') : t('bundle.contents_description')}
            </p>
            {isArchive && documentUid && <DepositExtractSummary org={org} documentUid={documentUid} />}
            <ul className="divide-y divide-border">
                {children.map((child) => (
                    <li key={child.uid} className="py-2">
                        <button
                            onClick={() =>
                                navigate({
                                    to: '/$org/documents/$docUid',
                                    params: { org, docUid: child.uid },
                                })
                            }
                            className="w-full flex items-center justify-between gap-2 hover:text-primary transition-colors text-left"
                            data-testid={`bundle-child-${child.uid}`}
                        >
                            <span className="flex items-center gap-2 min-w-0">
                                {!isArchive && (
                                    <Badge variant="outline" className="shrink-0" data-testid={`bundle-child-pages-${child.uid}`}>
                                        {t('bundle.child_pages', {
                                            range: formatPageRange(child.parent_document_pages),
                                            count: child.parent_document_pages?.length || 0,
                                        })}
                                    </Badge>
                                )}
                                <span className="truncate">{child.name}</span>
                                <span className="text-xs text-muted-foreground shrink-0">{typeName(child)}</span>
                            </span>
                            <AppIcon path={mdiOpenInNew} size="xs" className="opacity-70 shrink-0" />
                        </button>
                    </li>
                ))}
            </ul>
            {truncated && (
                <p className="text-xs text-muted-foreground mt-3" data-testid="bundle-children-truncated">
                    {t('archive.list_truncated', { shown: children.length, total: shownCount })}
                </p>
            )}
        </div>
    )
}
